import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search, Filter, Eye, Edit, Phone, DollarSign, MessageCircle, X, Sliders, Calendar } from 'lucide-react';
import { format } from 'date-fns';

import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';
import CreateJobModal from './CreateJobModal';
import { sendWhatsAppMessage } from '../../utils/whatsapp';

const Jobs = () => {
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Column Visibility States
  const DEFAULT_COLUMNS = {
    branch: true,
    jobNo: true,
    category: true,
    brand: true,
    model: true,
    priority: true,
  };

  const COLUMN_LIST = [
    { key: 'branch', label: 'Branch' },
    { key: 'jobNo', label: 'Job Number' },
    { key: 'category', label: 'Category' },
    { key: 'brand', label: 'Brand' },
    { key: 'model', label: 'Model' },
    { key: 'priority', label: 'Priority' },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('jobs_visible_columns_v2');
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });

  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [showDatePopup, setShowDatePopup] = useState(false);

  React.useEffect(() => {
    localStorage.setItem('jobs_visible_columns_v2', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  const toggleColumn = (colKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [colKey]: !prev[colKey]
    }));
  };
  const [paymentData, setPaymentData] = useState({
    jobId: '',
    amount: '',
    paymentMode: 'CASH',
    referenceNumber: '',
    notes: ''
  });

  // Fetch technicians for dropdown filter
  const { data: techniciansData } = useQuery(
    ['technicians', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/technicians', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch
    }
  );

  const { data, isLoading, refetch } = useQuery(
    ['jobs', selectedBranch?.id, page, search, status, priority, technicianFilter, startDateFilter, endDateFilter],
    async () => {
      const params = {
        page,
        limit: 10,
        branchId: selectedBranch?.id,
      };
      
      if (search) params.search = search;
      if (status) params.status = status;
      if (priority) params.priority = priority;
      if (technicianFilter) params.technicianName = technicianFilter;
      if (startDateFilter) params.startDate = startDateFilter;
      if (endDateFilter) params.endDate = endDateFilter;

      const response = await api.get('/api/jobs', { params });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
      keepPreviousData: true,
    }
  );

  // Get job counts by status
  const { data: statusCounts } = useQuery(
    ['job-status-counts', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/jobs/status-counts', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
    }
  );

  // Get company settings for STD code
  const { data: companySettings } = useQuery(
    'company-settings',
    async () => {
      const response = await api.get('/api/settings/company');
      return response.data.data;
    }
  );

  const jobs = data?.jobs || [];
  const pagination = data?.pagination || {};

  // Handle WhatsApp share
  const handleWhatsAppShare = (job, e) => {
    e.stopPropagation(); // Prevent row click navigation
    
    if (!job.customer?.phone) {
      toast.error('Customer phone number not available');
      return;
    }

    const template = selectedBranch?.whatsappTemplate;
    const stdCode = companySettings?.stdCode || '+91';

    const success = sendWhatsAppMessage(job, template, stdCode, currencySymbol);
    
    if (success) {
      toast.success('Opening WhatsApp...');
    } else {
      toast.error('Failed to open WhatsApp');
    }
  };

  // Create payment mutation
  const createPaymentMutation = useMutation(
    async (data) => {
      const response = await api.post('/api/receipts/job-payment', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Payment recorded successfully!');
        queryClient.invalidateQueries('jobs');
        setShowPaymentModal(false);
        resetPaymentForm();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to record payment');
      }
    }
  );

  const handlePaymentSubmit = (e) => {
    e.preventDefault();
    
    if (!paymentData.jobId || !paymentData.amount) {
      toast.error('Job and amount are required');
      return;
    }

    createPaymentMutation.mutate({
      ...paymentData,
      amount: parseFloat(paymentData.amount)
    });
  };

  const resetPaymentForm = () => {
    setPaymentData({
      jobId: '',
      amount: '',
      paymentMode: 'CASH',
      referenceNumber: '',
      notes: ''
    });
  };

  const statusCards = [
    { label: 'Received', value: 'PENDING', color: 'bg-blue-500', count: statusCounts?.PENDING || 0 },
    { label: 'Pending', value: 'PENDING', color: 'bg-red-500', count: statusCounts?.PENDING || 0 },
    { label: 'In Progress', value: 'IN_PROGRESS', color: 'bg-orange-500', count: statusCounts?.IN_PROGRESS || 0 },
    { label: 'Completed', value: 'COMPLETED', color: 'bg-green-500', count: statusCounts?.COMPLETED || 0 },
    { label: 'Delivered', value: 'DELIVERED', color: 'bg-purple-500', count: statusCounts?.DELIVERED || 0 },
    { label: 'Returned', value: 'RETURNED', color: 'bg-rose-500', count: statusCounts?.RETURNED || 0 },
    { label: 'Re-Entry', value: 'REENTRY', color: 'bg-yellow-500', count: statusCounts?.REENTRY || 0 },
  ];

  const getStatusBadge = (status) => {
    const statusClasses = {
      PENDING: 'status-badge status-pending',
      IN_PROGRESS: 'status-badge status-in-progress',
      COMPLETED: 'status-badge status-completed',
      DELIVERED: 'status-badge status-delivered',
      RETURNED: 'status-badge status-returned bg-rose-100 text-rose-800',
      REENTRY: 'status-badge status-reentry bg-yellow-100 text-yellow-800',
    };

    return (
      <span className={statusClasses[status] || 'status-badge'}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      LOW: 'status-badge priority-low',
      NORMAL: 'status-badge priority-normal',
      HIGH: 'status-badge priority-high',
      URGENT: 'status-badge priority-urgent',
    };

    return (
      <span className={priorityClasses[priority] || 'status-badge'}>
        {priority}
      </span>
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Job management</h1>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowPaymentModal(true)}
            className="bg-orange-100 hover:bg-orange-200 text-orange-800 font-semibold px-6 py-2 rounded-lg inline-flex items-center transition-colors"
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Add New Payment
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-orange-400 hover:bg-orange-500 text-white font-semibold px-6 py-2 rounded-lg inline-flex items-center transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New Job
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {statusCards.map((card) => (
          <button
            key={card.label}
            onClick={() => setStatus(status === card.value ? '' : card.value)}
            className={`${card.color} ${
              status === card.value ? 'ring-4 ring-offset-2 ring-blue-300' : ''
            } text-white rounded-lg p-4 hover:opacity-90 transition-all duration-200 shadow-md`}
          >
            <div className="text-3xl font-bold">{card.count}</div>
            <div className="text-sm font-medium mt-1">{card.label}</div>
          </button>
        ))}
      </div>

      {/* Redesigned Premium Filters Section */}
      <div className="bg-white rounded-xl shadow-md border border-gray-100 p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-800">Multiple Filters</h3>
          </div>
          <div className="flex items-center space-x-3 relative">
            <div className="relative">
              <button
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                className="text-xs text-gray-700 hover:text-gray-900 font-semibold flex items-center bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg border border-gray-300 transition-colors"
              >
                <Sliders className="w-3.5 h-3.5 mr-1" />
                Columns
              </button>
              {showColumnDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-3 max-h-80 overflow-y-auto">
                  <div className="flex items-center justify-between border-b pb-2 mb-2">
                    <span className="text-xs font-bold text-gray-850">Visible Columns</span>
                    <button
                      onClick={() => setVisibleColumns(DEFAULT_COLUMNS)}
                      className="text-[10px] text-blue-600 hover:underline font-bold"
                    >
                      Reset
                    </button>
                  </div>
                  <div className="space-y-2">
                    {COLUMN_LIST.map((col) => (
                      <label key={col.key} className="flex items-center space-x-2.5 text-xs text-gray-700 font-medium cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors">
                        <input
                          type="checkbox"
                          checked={visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-3.5 w-3.5 cursor-pointer"
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {(search || status || priority || technicianFilter || startDateFilter || endDateFilter) && (
              <button
                onClick={() => {
                  setSearch('');
                  setStatus('');
                  setPriority('');
                  setTechnicianFilter('');
                  setStartDateFilter('');
                  setEndDateFilter('');
                  setTempStartDate('');
                  setTempEndDate('');
                  setPage(1);
                }}
                className="text-xs text-red-500 hover:text-red-700 font-semibold flex items-center bg-red-50 px-3 py-1.5 rounded-lg border border-red-200 transition-colors"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Reset All Filters
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Text Search Box */}
          <div className="relative lg:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Search Keywords</label>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search Customer name, phone, job#, brand, model, tech..."
                className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            </div>
          </div>

          {/* Status Dropdown */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Job Status</label>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Statuses</option>
              <option value="PENDING">Pending</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="DELIVERED">Delivered</option>
              <option value="RETURNED">Returned</option>
              <option value="REENTRY">Re-Entry</option>
            </select>
          </div>

          {/* Priority Dropdown */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Priority</label>
            <select
              value={priority}
              onChange={(e) => { setPriority(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Priorities</option>
              <option value="NORMAL">Normal</option>
              <option value="MEDIUM">Medium</option>
              <option value="HIGH">High</option>
              <option value="CRITICAL">Critical</option>
            </select>
          </div>

          {/* Technician Dropdown */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Technician</label>
            <select
              value={technicianFilter}
              onChange={(e) => { setTechnicianFilter(e.target.value); setPage(1); }}
              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Technicians</option>
              {techniciansData?.map(tech => (
                <option key={tech.id} value={tech.name}>{tech.name}</option>
              ))}
            </select>
          </div>

          {/* Received Date Range Button with Popover */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Received Date Range</label>
            <button
              onClick={() => {
                setTempStartDate(startDateFilter);
                setTempEndDate(endDateFilter);
                setShowDatePopup(!showDatePopup);
              }}
              className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between transition-colors"
            >
              <span className="text-gray-700">
                {startDateFilter && endDateFilter
                  ? `${format(new Date(startDateFilter), 'dd/MM/yyyy')} to ${format(new Date(endDateFilter), 'dd/MM/yyyy')}`
                  : startDateFilter
                  ? `From ${format(new Date(startDateFilter), 'dd/MM/yyyy')}`
                  : endDateFilter
                  ? `Until ${format(new Date(endDateFilter), 'dd/MM/yyyy')}`
                  : 'Select Date Range'}
              </span>
              <Calendar className="w-4 h-4 text-gray-400" />
            </button>

            {showDatePopup && (
              <div className="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50 p-4 space-y-4">
                <div className="flex items-center justify-between border-b pb-2">
                  <span className="text-xs font-bold text-gray-800">Filter by Date Range</span>
                  <button
                    onClick={() => setShowDatePopup(false)}
                    className="text-gray-400 hover:text-gray-650 text-sm"
                  >
                    ×
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Start Date</label>
                    <input
                      type="date"
                      value={tempStartDate}
                      onChange={(e) => setTempStartDate(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">End Date</label>
                    <input
                      type="date"
                      value={tempEndDate}
                      onChange={(e) => setTempEndDate(e.target.value)}
                      className="w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-end space-x-2 pt-2 border-t">
                  <button
                    onClick={() => {
                      setTempStartDate('');
                      setTempEndDate('');
                      setStartDateFilter('');
                      setEndDateFilter('');
                      setPage(1);
                      setShowDatePopup(false);
                    }}
                    className="px-3 py-1.5 text-xs text-red-600 bg-red-50 hover:bg-red-100 rounded-md font-semibold transition-colors"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => {
                      setStartDateFilter(tempStartDate);
                      setEndDateFilter(tempEndDate);
                      setPage(1);
                      setShowDatePopup(false);
                    }}
                    className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-md font-semibold transition-colors shadow-sm"
                  >
                    OK
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Jobs Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">S.NO</th>
                {visibleColumns.branch && <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">BRANCH</th>}
                {visibleColumns.jobNo && <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">JOB_NO</th>}
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">JOB_DATE</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">CUST_NAME</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">CONTACT</th>
                {visibleColumns.category && <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">CATEGORY</th>}
                {visibleColumns.brand && <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">BRAND</th>}
                {visibleColumns.model && <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">MODEL</th>}
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">STATUS</th>
                {visibleColumns.priority && <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">PRIORITY</th>}
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ESTIMATE_COST</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">PAID_AMOUNT</th>
                <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">ACTION</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {jobs.map((job, index) => (
                <tr key={job.id} className="hover:bg-blue-50 cursor-pointer" onClick={() => window.location.href = `/jobs/${job.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {((pagination.currentPage || page) - 1) * 10 + index + 1}
                  </td>
                  {visibleColumns.branch && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {selectedBranch?.name || 'Delhi'}
                    </td>
                  )}
                  {visibleColumns.jobNo && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-blue-600">
                      {job.jobNumber}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {format(new Date(job.createdAt), 'dd/MM/yyyy')}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {job.customer.name}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {job.customer.phone}
                  </td>
                  {visibleColumns.category && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.deviceCategory || 'N/A'}
                    </td>
                  )}
                  {visibleColumns.brand && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.deviceBrand}
                    </td>
                  )}
                  {visibleColumns.model && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {job.deviceModel}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      job.status === 'PENDING' ? 'bg-red-100 text-red-800' :
                      job.status === 'IN_PROGRESS' ? 'bg-orange-100 text-orange-800' :
                      job.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                      job.status === 'DELIVERED' ? 'bg-purple-100 text-purple-800' :
                      job.status === 'RETURNED' ? 'bg-rose-100 text-rose-800' :
                      job.status === 'REENTRY' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {job.status.replace('_', ' ')}
                    </span>
                  </td>
                  {visibleColumns.priority && (
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getPriorityBadge(job.priority || 'NORMAL')}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {currencySymbol}{(job.actualCost || job.estimatedCost).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-right">
                    {currencySymbol}{job.advanceAmount.toFixed(2)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-center">
                    <button
                      onClick={(e) => handleWhatsAppShare(job, e)}
                      className="inline-flex items-center justify-center p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                      title="Send WhatsApp Message"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {((pagination.currentPage - 1) * 10) + 1} to{' '}
                {Math.min(pagination.currentPage * 10, pagination.totalCount)} of{' '}
                {pagination.totalCount} results
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={!pagination.hasPrev}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={!pagination.hasNext}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {jobs.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <p className="text-lg font-medium">No jobs found</p>
            <p className="mt-1">Get started by creating your first job.</p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Job
            </button>
          </div>
        </div>
      )}

      {/* Create Job Modal */}
      <CreateJobModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => refetch()}
      />

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h2 className="text-xl font-bold">Add Payment</h2>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  resetPaymentForm();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
              >
                ×
              </button>
            </div>

            <form onSubmit={handlePaymentSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Job *
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={paymentData.jobId}
                  onChange={(e) => setPaymentData({ ...paymentData, jobId: e.target.value })}
                  required
                >
                  <option value="">Select a job</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.jobNumber} - {job.customer.name} ({job.deviceBrand} {job.deviceModel})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Amount *
                </label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={paymentData.amount}
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                  step="0.01"
                  min="0"
                  placeholder="Enter amount"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Mode *
                </label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={paymentData.paymentMode}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentMode: e.target.value })}
                  required
                >
                  <option value="CASH">Cash</option>
                  <option value="BANK">Bank Transfer</option>
                  <option value="UPI">UPI</option>
                  <option value="CARD">Card</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference Number
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  value={paymentData.referenceNumber}
                  onChange={(e) => setPaymentData({ ...paymentData, referenceNumber: e.target.value })}
                  placeholder="Transaction/Reference number (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows="3"
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  placeholder="Additional notes (optional)"
                />
              </div>

              <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    resetPaymentForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createPaymentMutation.isLoading}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {createPaymentMutation.isLoading ? (
                    <>
                      <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Recording...
                    </>
                  ) : (
                    'Record Payment'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
